import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CampaignSetup = ({ onCampaignUpdate }) => {
  const [campaignData, setCampaignData] = useState({
    name: '',
    subject: '',
    mediaList: '',
    sendTime: 'immediate',
    followUp: false,
    followUpDelay: '3'
  });

  const mediaListOptions = [
    { value: 'book-reviewers', label: 'Book Reviewers (245 contacts)' },
    { value: 'literary-journalists', label: 'Literary Journalists (189 contacts)' },
    { value: 'podcast-hosts', label: 'Podcast Hosts (156 contacts)' },
    { value: 'bloggers', label: 'Book Bloggers (312 contacts)' },
    { value: 'custom-list', label: 'Custom List (87 contacts)' }
  ];

  const sendTimeOptions = [
    { value: 'immediate', label: 'Send Immediately' },
    { value: 'optimal', label: 'Optimal Time (AI Suggested)' },
    { value: 'scheduled', label: 'Schedule for Later' },
    { value: 'timezone', label: 'Recipient Time Zones' }
  ];

  const followUpDelayOptions = [
    { value: '1', label: '1 day' },
    { value: '3', label: '3 days' },
    { value: '7', label: '1 week' },
    { value: '14', label: '2 weeks' }
  ];

  const handleInputChange = (field, value) => {
    const updatedData = { ...campaignData, [field]: value };
    setCampaignData(updatedData);
    onCampaignUpdate?.(updatedData);
  };

  const getRecipientCount = () => {
    const selected = mediaListOptions?.find(opt => opt?.value === campaignData?.mediaList);
    if (!selected) return 0;
    const match = selected?.label?.match(/\((\d+) contacts\)/);
    return match ? parseInt(match?.[1]) : 0;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 h-fit">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-8 h-8 bg-primary rounded-md flex items-center justify-center">
          <Icon name="Settings" size={16} color="white" />
        </div>
        <h2 className="text-lg font-heading font-semibold text-foreground">Campaign Setup</h2>
      </div>
      <div className="space-y-6">
        {/* Campaign Name */}
        <Input
          label="Campaign Name"
          type="text"
          placeholder="e.g., New Release Announcement - October 2024"
          value={campaignData?.name}
          onChange={(e) => handleInputChange('name', e?.target?.value)}
          required
        />

        {/* Subject Line */}
        <div>
          <Input
            label="Subject Line"
            type="text"
            placeholder="Enter email subject line"
            value={campaignData?.subject}
            onChange={(e) => handleInputChange('subject', e?.target?.value)}
            required
          />
          <div className="mt-2 flex items-center space-x-2">
            <Button variant="outline" size="sm" iconName="TestTube" iconPosition="left">
              A/B Test
            </Button>
            <Button variant="ghost" size="sm" iconName="Zap" iconPosition="left">
              AI Optimize
            </Button>
          </div>
        </div>

        {/* Media List Selection */}
        <Select
          label="Target Media List"
          description="Select the media contacts for this campaign"
          options={mediaListOptions}
          value={campaignData?.mediaList}
          onChange={(value) => handleInputChange('mediaList', value)}
          placeholder="Choose media list"
          required
        />

        {/* Recipient Count Display */}
        {campaignData?.mediaList && (
          <div className="bg-muted rounded-md p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Icon name="Users" size={16} className="text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">Recipients Selected</span>
              </div>
              <span className="text-lg font-semibold text-primary">{getRecipientCount()}</span>
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              Estimated delivery cost: ${(getRecipientCount() * 0.02)?.toFixed(2)}
            </div>
          </div>
        )}

        {/* Send Timing */}
        <Select
          label="Send Timing"
          description="Choose when to send this campaign"
          options={sendTimeOptions}
          value={campaignData?.sendTime}
          onChange={(value) => handleInputChange('sendTime', value)}
        />

        {/* Scheduled Time Input */}
        {campaignData?.sendTime === 'scheduled' && (
          <Input
            label="Schedule Date & Time"
            type="datetime-local"
            value=""
            onChange={(e) => handleInputChange('scheduledTime', e?.target?.value)}
          />
        )}

        {/* Follow-up Settings */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-foreground">Automated Follow-up</label>
              <p className="text-xs text-muted-foreground">Send follow-up to non-responders</p>
            </div>
            <button
              onClick={() => handleInputChange('followUp', !campaignData?.followUp)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                campaignData?.followUp ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  campaignData?.followUp ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {campaignData?.followUp && (
            <Select
              label="Follow-up Delay"
              options={followUpDelayOptions}
              value={campaignData?.followUpDelay}
              onChange={(value) => handleInputChange('followUpDelay', value)}
            />
          )}
        </div>

        {/* Campaign Actions */}
        <div className="pt-4 border-t border-border space-y-3">
          <Button variant="outline" fullWidth iconName="Save" iconPosition="left">
            Save as Draft
          </Button>
          <Button variant="default" fullWidth iconName="Eye" iconPosition="left">
            Preview Campaign
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CampaignSetup;